import FormTitle from '@core/components/FormTitle';
import { TextInput } from '@core/components/Inputs';
import ReadOnlyInput from '@core/components/Inputs/ReadOnlyInput';
import { SelectInput } from '@core/components/Select';
import { alterarParcelasSubsequentes, calculaDiferencaParcelasUtil, gerarParcelas } from '@core/util/parcelaUtil';
import FuseUtils from '@fuse/utils';
import { Grid, makeStyles } from '@material-ui/core';
import { Categorias } from 'app/components/Categorias';
import { getCurrency } from 'app/utils/monetarioUtil';
import { useFormikContext } from 'formik';
import moment from 'moment';
import NP from 'number-precision';
import React, { useEffect } from 'react';
import CompraAnexo from '../../CompraAnexo';
import CompraFormaPagamento from '../../CompraFormaPagamento';
import CompraInformacoesNotaFiscal from '../../CompraInformacoesNotaFiscal';
import DadosCompra from '../../DadosCompra';
import { CamposAproveitamentoCredito } from '../CompraAproveitamentoCredito';

const useStyles = makeStyles({
  valorTotal: {
    width: '100%',
    marginTop: 8,
    '& > label': {
      marginBottom: 8,
      fontSize: 24,
      fontWeight: 500,
      color: '#111',
    },
  },
});

const CompraServicoForm = ({ empresaIbsCbs }) => {
  const formik = useFormikContext();

  const styles = useStyles();

  let compraPaga = false;

  if (formik.values?.pagamento?.condicaoPagamento === 'A_VISTA') {
    compraPaga = !!formik.values?.pagamento?.avista?.contaBaixada;
  } else if (
    formik.values?.pagamento?.condicaoPagamento === 'PARCELADO' &&
    formik.values?.pagamento?.parcelado?.parcelas?.length
  ) {
    compraPaga = !!formik.values?.pagamento?.parcelado?.parcelas
      ?.map((parcela) => parcela?.dataPagamento)
      ?.some((dataPagamento) => !!dataPagamento);
  }

  if (formik.values?.compraServico?.notaFiscal?.retencoes?.length) {
    compraPaga =
      compraPaga ||
      formik.values?.compraServico?.notaFiscal?.retencoes
        ?.map((retencao) => retencao?.contaBaixada)
        ?.some((contaBaixada) => !!contaBaixada);
  }

  const resetParcelas = () => {
    if (formik.values?.pagamento?.parcelado?.parcelas?.length) {
      formik.setFieldValue(`pagamento.parcelado.parcelas`, []);
      formik.setFieldValue('pagamento.parcelado.diferencaParcelas', 0);
    }
  };

  /*
   * RNE-228 - Exibição da informação de "Diferença" : Public <<Functional>> Feature 
   * O sistema deve manter a exibição da informação "Diferença" quando o somatório dos valores das parcelas for diferente do valor líquido.

   * Diferença = Valor líquido - Somatório dos valores informados nas parcelas.
   */
  const calculaDiferencaParcelas = (e) => {
    let diferencaParcelas = 0;
    let index = -1;

    if (e?.target?.id?.includes('valorParcela')) {
      index = parseInt(e.target.id.substring(e.target.id.indexOf(`[`) + 1, e.target.id.indexOf(']')));
    }

    if (formik.values.pagamento?.parcelado?.parcelas?.length) {
      let parcelas = formik.values.pagamento?.parcelado?.parcelas || [];
      const { valorLiquido } = formik.values;

      parcelas = parcelas.map((parcela, i) => {
        if (index === i) {
          return { ...parcela, valorParcela: e.target.value };
        }
        return parcela;
      });

      diferencaParcelas = calculaDiferencaParcelasUtil(parcelas, valorLiquido);

      formik.setFieldValue('pagamento.parcelado.diferencaParcelas', diferencaParcelas);
    }
  };

  /*
   * RNE-213 - Alteração das informações referentes às parcelas - compra de produto : Public <<Functional>> Feature
   * Se a data da compra ou a data da primeira parcela for alterada, o sistema deve alterar as datas das outras parcelas também, considerando sempre cada parcela de 30 em 30 dias.
   */
  const handleChangeDataPrimeiraParcela = (dataVencimento) => {
    const parcelas = formik.values?.pagamento?.parcelado?.parcelas;
    if (dataVencimento && parcelas?.length) {
      const primeiraDataVencimento = dataVencimento.clone();

      const novasParcelas = alterarParcelasSubsequentes(parcelas, primeiraDataVencimento);

      formik.setFieldValue('pagamento.parcelado.parcelas', novasParcelas);
    }
  };

  const handleGerarParcelas = () => {
    const quantidade = formik.values?.pagamento?.parcelado?.quantidade || 0;
    const dataPrimeiraParcela = formik?.values?.dataCompra.clone().add(30, 'day') || moment();

    const { valorLiquido } = formik.values;

    const parcelas = gerarParcelas(quantidade, dataPrimeiraParcela, valorLiquido);

    formik.setFieldValue(`pagamento.parcelado.parcelas`, parcelas);
  };

  const handleChangeConta = (id, value) => {
    formik.setFieldValue(id, value);
  };

  const handleChangeCondicaoPagamento = (e) => {
    formik.setFieldValue('pagamento.avista.condicaoPagamento', e.target.value);
    formik.setFieldValue('pagamento.parcelado.condicaoPagamento', e.target.value);

    formik.handleChange(e);
  };

  const handleChangePagamento = (e) => {
    formik.handleChange(e);

    if (e?.target?.id === 'pagamento.condicaoPagamento') {
      if (e?.target?.value === 'A_VISTA') {
        formik.setFieldValue('pagamento.avista.condicaoPagamento', e.target.value);
      }
    }

    if (e?.target?.id === 'pagamento.avista.compraPaga') {
      // o switch e o formik fazem com que o true vire "false"
      if (e.target.value === 'false') {
        formik.setFieldValue('pagamento.avista.dataPagamento', formik.values.dataCompra);
        formik.setFieldValue('pagamento.avista.dataLiquidacao', formik.values.dataCompra);
      }
    }

    if (e?.target?.id?.includes('pagamento.parcelado.parcelas') && e?.target?.id?.includes('valorParcela')) {
      calculaDiferencaParcelas(e);
    }
    if (e?.target?.id?.includes('quantidade')) {
      resetParcelas();
    }
  };

  const handleChangeTemRetencao = (e) => {
    // Pq o Switch inverte os valores e retorna string??
    if (e.target.value === 'false') {
      formik.setFieldValue('compraServico.notaFiscal.retencoes', [
        { key: FuseUtils.generateGUID(), dataVencimento: null, contribuinte: 'FORNECEDOR' },
      ]);
    } else {
      formik.setFieldValue('compraServico.notaFiscal.retencoes', []);
      formik.setFieldValue('valorLiquido', formik.values?.compraServico?.notaFiscal?.valorBruto || 0);
    }

    formik.handleChange(e);
  };

  const handleAddRetencao = () => {
    const { retencoes } = formik.values.compraServico?.notaFiscal;

    retencoes.push({ key: FuseUtils.generateGUID(), dataVencimento: null, contribuinte: 'FORNECEDOR' });

    formik.setFieldValue('compraServico.notaFiscal.retencoes', retencoes);
  };

  const getValorBruto = (e, indice = -1) => {
    let valorBruto = 0;

    formik.values.categorias.forEach((categoria, i) => {
      if (indice !== i) {
        valorBruto += categoria.valorBruto ? categoria.valorBruto : 0;
      } else {
        valorBruto += e.target.value ? e.target.value : 0;
      }
    });

    return valorBruto;
  };

  const geraValorLiquido = (e, indice = -1) => {
    const retencoes = formik.values?.compraServico?.notaFiscal?.retencoes || [];
    const valorBruto = getValorBruto(e, indice);

    // se o que estiver sendo mudado for o imposto, utiliza o valor do evento,
    // pois o formik.values ainda não foi atualizado
    let index = -1;
    let campo = '';

    if (e?.target?.id?.includes('retencoes[') && e?.target?.id?.includes('imposto')) {
      index = parseInt(e.target.id.substring(e.target.id.indexOf(`[`) + 1, e.target.id.indexOf(']')));
      campo = 'imposto';
    }

    if (e?.target?.id?.includes('retencoes[') && e?.target?.id?.includes('valor')) {
      index = parseInt(e.target.id.substring(e.target.id.indexOf(`[`) + 1, e.target.id.indexOf(']')));
      campo = 'valor';
    }

    const valorImpostos =
      retencoes
        .map((retencao, i) => {
          if (i === index) {
            // se o que mudou foi o imposto, faz o calculo pela aliquota, já que o
            // formik.values ainda não foi atualizado
            if (campo === 'imposto') {
              return NP.times(NP.divide(e.target.value?.aliquota || 0, 100), valorBruto);
            }
            return e.target.value;
          }

          const valorImposto = NP.times(NP.divide(retencao?.imposto?.aliquota || 0, 100), valorBruto);

          formik.setFieldValue(`compraServico.notaFiscal.retencoes[${i}].valor`, valorImposto);

          return valorImposto;
        })
        .reduce((acc, valor) => NP.plus(acc, valor || 0), 0) || 0;

    const valorLiquido = NP.minus(valorBruto, valorImpostos);

    formik.setFieldValue('valorLiquido', valorLiquido);
    resetParcelas();
  };

  const geraValorImposto = (e) => {
    const valorBruto = getValorBruto();

    const aliquota = NP.divide(e.target.value?.aliquota || 0, 100);

    const valorImposto = NP.round(NP.times(valorBruto, aliquota), 2);

    const key = e.target.id.replace('imposto', 'valor');

    formik.setFieldValue(key, valorImposto);
  };

  const handleChangeNotaFiscal = (e) => {
    formik.handleChange(e);
    if (e?.target?.id?.includes('retencoes') && e?.target?.id?.includes('imposto')) {
      geraValorImposto(e);
    }

    if (e?.target?.id?.includes('retencoes') || e?.target?.id?.includes('valorBruto')) {
      geraValorLiquido(e);
    }

    if (e.target.id.includes('valorBruto')) {
      resetParcelas();
    }
  };

  const valorLiquidoText = getCurrency(formik.values?.valorLiquido || '');

  const handleAddAnexo = (anexo) => {
    const { anexos = [] } = formik.values;

    anexos.push(anexo);

    // formik.setFieldValue(`anexos[${anexos.length - 1}]`, anexo);
    formik.handleChange({ target: { id: `anexos[${anexos.length - 1}]`, value: anexo } });

    // o Formik não seta o formulário como dirty na linha de cima
    formik.handleChange({ target: { id: 'random', value: Math.random() } });
  };

  const handleRemoveAnexo = (anexo) => {
    const { anexos = [] } = formik.values;

    const novosAnexos = anexos.filter((a) => a?.id !== anexo?.id);

    formik.handleChange({ target: { id: 'anexos', value: novosAnexos } });
  };

  const handlePreencherDataVencimento = (dataCompra) => {
    const dataCompraClonado = dataCompra.clone();
    if (dataCompra) {
      formik.setFieldValue(`pagamento.avista.dataVencimento`, dataCompraClonado);
    }
  };

  const _errors = {
    categorias: formik.values?.categorias?.map((_categoria, index) => {
      return {
        classe:
          (formik.submitCount > 0 || (formik.touched?.categorias && formik.touched?.categorias[index]?.classe)) &&
          formik.errors?.categorias &&
          formik.errors?.categorias[index]?.classe,
        valorBruto:
          (formik.submitCount > 0 || (formik.touched?.categorias && formik.touched?.categorias[index]?.valorBruto)) &&
          formik.errors?.categorias &&
          formik.errors?.categorias[index]?.valorBruto,
      };
    }),
  };

  const onChangeTipoAproveitamentoCredito = (event) => {
    formik.handleChange(event);
    formik.setFieldValue('aproveitamentoCredito.aliquotaIbs', '');
    formik.setFieldValue('aproveitamentoCredito.aliquotaCbs', '');
    formik.setFieldValue('aproveitamentoCredito.observacaoJustificativa', '');
    if (event.target.value === 'CREDITO_INTEGRAL') {
      formik.setFieldValue('aproveitamentoCredito.percentualCredito', 100);
    } else {
      formik.setFieldValue('aproveitamentoCredito.percentualCredito', '');
    }
  };

  const categoriasLength = formik.values?.categorias?.length;
  useEffect(() => {
    if (categoriasLength > 1) {
      formik.setFieldValue('compraServico.notaFiscal.temRetencao', false);
      formik.setFieldValue('compraServico.notaFiscal.retencoes', []);

      const valorLiquido = formik.values.categorias.reduce((soma, categoria) => {
        if (categoria.valorBruto) {
          return soma + categoria.valorBruto;
        }
        return soma;
      }, 0);
      formik.setFieldValue('valorLiquido', valorLiquido);
    }
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriasLength]);

  const valorTotalCategorias = formik.values?.categorias.reduce((acc, categoria) => {
    const valorItem = Number(categoria?.valorBruto) || 0;
    return NP.plus(acc, valorItem);
  }, 0);

  return (
    <>
      <DadosCompra
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        onChangeDataPrimeiraParcela={handleChangeDataPrimeiraParcela}
        values={formik.values}
        touched={formik.touched}
        errors={formik.errors}
        submitCount={formik.submitCount}
        compraPaga={compraPaga}
        onChangePreencherDataVencimento={handlePreencherDataVencimento}
      />

      <Grid container spacing={3} className="mt-16">
        <Categorias
          categorias={formik.values?.categorias}
          handleChange={formik.handleChange}
          handleChangeValorBruto={(event, index) => {
            formik.handleChange(event);
            resetParcelas();
            geraValorLiquido(event, index);
          }}
          handleBlur={formik.handleBlur}
          disabledClasse={compraPaga}
          disabledValor={compraPaga}
          errors={_errors}
          natureza="DESPESA"
          showAlertRetencao
        />
      </Grid>

      <Grid item xs={12} className="mt-16 mb-16">
        <TextInput
          id="compraServico.descricao"
          name="compraServico.descricao"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values?.compraServico?.descricao}
          // error={_errors.descricao}
          touched={formik.touched.descricao}
          // required={!!_errors.descricao}
          maxLength={1024}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label="Descrição detalhada do serviço"
          disabled={compraPaga}
        />
      </Grid>
      <Grid item xs={12} className="mt-16 mb-16">
        <TextInput
          id="observacao"
          name="observacao"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values?.observacao}
          touched={formik.touched.observacao}
          maxLength={500}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label="Observação"
          disabled={compraPaga}
        />
      </Grid>

      <CompraInformacoesNotaFiscal
        id="compraServico.notaFiscal"
        name="compraServico.notaFiscal"
        onBlur={formik.handleBlur}
        onChange={handleChangeNotaFiscal}
        values={formik.values.compraServico?.notaFiscal}
        touched={formik.touched.compraServico?.notaFiscal}
        errors={formik.errors.compraServico?.notaFiscal}
        fornecedor={formik.values?.fornecedor}
        minDate={formik.values?.dataCompra}
        onAddRetencao={handleAddRetencao}
        onChangeTemRetencao={handleChangeTemRetencao}
        submitCount={formik.submitCount}
        compraPaga={compraPaga}
        compraId={formik.values?.id}
        categorias={formik.values.categorias}
      />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ReadOnlyInput
            className={styles.valorTotal}
            id="valorLiquido"
            label="Valor líquido(R$)"
            value={valorLiquidoText}
          />
        </Grid>
      </Grid>

      {empresaIbsCbs && (
        <>
          <FormTitle title="Aproveitamento de crédito IBS/CBS" />
          <Grid container spacing={3} style={{ marginTop: 4, marginBottom: 8 }}>
            <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
              <SelectInput
                id="aproveitamentoCredito.tipoAproveitamentoCredito"
                label="Aproveitamento de crédito?"
                value={formik.values?.aproveitamentoCredito?.tipoAproveitamentoCredito}
                onChange={(event) => {
                  onChangeTipoAproveitamentoCredito(event);
                }}
                onBlur={formik.handleBlur}
                options={[
                  { id: 'NAO_APROVEITA_CREDITO', title: 'Não aproveita crédito' },
                  { id: 'CREDITO_INTEGRAL', title: 'Crédito integral' },
                  { id: 'CREDITO_PARCIAL', title: 'Crédito parcial' },
                ]}
                touched={!!formik.errors.aproveitamentoCredito?.tipoAproveitamentoCredito}
                error={formik.errors.aproveitamentoCredito?.tipoAproveitamentoCredito}
                required
                disabled={compraPaga}
              />
            </Grid>
            {formik?.values?.aproveitamentoCredito?.tipoAproveitamentoCredito &&
              formik?.values?.aproveitamentoCredito?.tipoAproveitamentoCredito !== 'NAO_APROVEITA_CREDITO' && (
                <CamposAproveitamentoCredito
                  formik={formik}
                  valorTotalCategorias={valorTotalCategorias}
                  disabled={compraPaga}
                />
              )}
          </Grid>
        </>
      )}

      <CompraFormaPagamento
        id="pagamento"
        name="pagamento"
        disabled={!!formik.values.id}
        onBlur={formik.handleBlur}
        onChange={handleChangePagamento}
        values={formik.values.pagamento}
        touched={formik.touched.pagamento}
        errors={formik.errors.pagamento}
        onGerarParcelas={handleGerarParcelas}
        onChangeConta={handleChangeConta}
        onChangeCondicaoPagamento={handleChangeCondicaoPagamento}
        onChangeDataPrimeiraParcela={handleChangeDataPrimeiraParcela}
        submitCount={formik.submitCount}
        minDate={formik.values?.dataCompra}
        compraPaga={compraPaga}
        valorTotal={formik.values?.valorLiquido}
        compraId={formik.values?.id}
      />

      <CompraAnexo id="anexos" values={formik?.values?.anexos} onAdd={handleAddAnexo} onRemove={handleRemoveAnexo} />
    </>
  );
};

export default CompraServicoForm;
